import React, { FC, useContext, useEffect, useState } from 'react'
import { joinClasses } from '@utils/styles'
import * as formUtils from '@templates/requests/__create.utils'
import parse from 'html-react-parser'
import {
	AppContextProps,
	AppStateContext
} from '@components/layouts/DynamicLayout'
import { formatHtmlForStrapiText } from '@utils/methods'
import Modal from '@components/ui/modal'
import {
	applyGoogleConsentMode,
	persistConsentStatus,
	readStoredAnalyticsConsent,
	syncGoogleAnalyticsScript
} from '../../../utils/googleConsent'
import {
	classes,
	getSessionConsent,
	setSessionConsent
} from './__cookiesConsentBanner.utils'

const OPEN_COOKIES_PREFERENCES_EVENT = 'open-cookies-preferences'

const CookiesConsentBanner: FC = ({}) => {
	const [isPreferencesOpen, setIsPreferencesOpen] = useState<boolean>(false)
	const { pageData } = useContext<AppContextProps>(AppStateContext)
	const [cookiesEnabled, setCookiesEnabled] = useState<boolean>(
		getSessionConsent()
	)
	const [analyticsConsent, setAnalyticsConsent] = useState<boolean>(true)
	const consentBannerMessage = pageData?.assets?.label_consent_banner_message
	const consentBannerMessageHtml = formatHtmlForStrapiText(
		typeof consentBannerMessage === 'string' ? consentBannerMessage : ''
	) as string
	const parsedConsentBannerMessage = parse(
		consentBannerMessageHtml
	)

	const commitAnalyticsConsent = (value: boolean) => {
		const status = value ? 'accepted' : 'refused'
		const hasChanged = persistConsentStatus(status)

		if (hasChanged) {
			applyGoogleConsentMode(status)
		}

		syncGoogleAnalyticsScript()
	}

	useEffect(() => {
		const consentGiven = readStoredAnalyticsConsent()

		if (consentGiven !== null) {
			setAnalyticsConsent(consentGiven)
		}
	}, [])

	useEffect(() => {
		const onOpenPreferences = () => {
			onSelectIsPreferenceOpen(true)
		}

		window.addEventListener(OPEN_COOKIES_PREFERENCES_EVENT, onOpenPreferences)

		return () => {
			window.removeEventListener(
				OPEN_COOKIES_PREFERENCES_EVENT,
				onOpenPreferences
			)
		}
	}, [])

	//sets cookie value without use effect. The cookies are enabled only if true. Else the cookie isnt created.
	const onSelectCookiesEnabled = (value: boolean) => {
		setCookiesEnabled(value)

		if (value) {
			setSessionConsent()
			commitAnalyticsConsent(analyticsConsent)
		}

		setIsPreferencesOpen(false)
	}

	const onSelectAllEnabled = () => {
		setCookiesEnabled(true)
		setAnalyticsConsent(true)

		setSessionConsent()
		commitAnalyticsConsent(true)
		setIsPreferencesOpen(false)
	}

	const onSelectAnalyticsConsent = (value: boolean) => {
		setAnalyticsConsent(value)
	}

	const onSelectIsPreferenceOpen = (value: boolean) => {
		setIsPreferencesOpen(value)

		if (readStoredAnalyticsConsent() === null) {
			setAnalyticsConsent(false)
		}
	}

	return (
		<>
			{!cookiesEnabled && !isPreferencesOpen && (
				<div className={classes.consentWraper}>
					<div className={classes.consentBanner}>
						<section className={classes.flexContainer}>
							<div className={classes.textContainer}>
								<b>{pageData?.assets?.label_consent_banner_title}</b>
								<br />
								{parsedConsentBannerMessage}
							</div>

							<div className={classes.buttonContainer}>
								<a
									className={joinClasses([
										formUtils.classes.btn,
										formUtils.classes.button,
										formUtils.classes.submitBtn
									])}
									onClick={() => {
										onSelectAllEnabled()
									}}
								>
									{pageData?.assets?.label_accept_cookies}
								</a>
								<a
									className={joinClasses([
										formUtils.classes.btn,
										formUtils.classes.button,
										formUtils.classes.buttonOutline
									])}
									style={{ marginLeft: '10px' }}
									onClick={() => {
										onSelectIsPreferenceOpen(true)
									}}
								>
									{pageData?.assets?.label_page_manage_prefs}
								</a>
							</div>
						</section>
					</div>
				</div>
			)}
			<Modal
				isOpen={isPreferencesOpen}
				onCancelBtn={() => {
					setIsPreferencesOpen(false)
				}}
				onCloseModal={() => setIsPreferencesOpen(false)}
				onSetIsOpen={() => {}}
				showExitBtn
				title={<b>{pageData?.assets?.label_consent_banner_title}</b>}
			>
				<>
					{parsedConsentBannerMessage}
					<div className={classes.checkboxWrapper}>
						<label className={classes.checkBoxLabel}>
							<input
								type="checkbox"
								checked
								disabled
								className={joinClasses([
									classes.checkBox,
									classes.checkboxChecked
								])}
							/>
							<b>
								{pageData?.assets?.label_consent_banner_functionnal_tools}
							</b>
							<span>
								{pageData?.assets?.label_cookies_consent_always_active}
							</span>
						</label>
						<div>
							<p>
								{
									pageData?.assets
										?.label_consent_banner_functionnal_tools_description
								}
							</p>
						</div>
					</div>
					<div className={classes.checkboxWrapper}>
						<label className={classes.checkBoxLabel}>
							<input
								type="checkbox"
								checked={analyticsConsent}
								onClick={(e: any) => {
									onSelectAnalyticsConsent(e.target.checked)
								}}
								className={classes.checkBox}
							/>
							<b> {pageData?.assets?.label_consent_banner_analytics}</b>
						</label>
						<div>
							<p>
								{
									pageData?.assets
										?.label_consent_banner_analytics_description
								}
							</p>
						</div>
					</div>

					<div
						className={classes.buttonContainer}
						style={{ marginTop: '20px', gap: '10px' }}
					>
						<a
							className={joinClasses([
								formUtils.classes.btn,
								formUtils.classes.button,
								formUtils.classes.buttonOutline
							])}
							onClick={() => {
								onSelectAllEnabled()
							}}
							style={{ margin: '0' }}
						>
							{pageData?.assets?.label_accept_all}
						</a>
						<a
							className={joinClasses([
								formUtils.classes.btn,
								formUtils.classes.button,
								formUtils.classes.submitBtn
							])}
							onClick={() => {
								onSelectCookiesEnabled(true)
							}}
							style={{ margin: '0' }}
						>
							{pageData?.assets?.label_save_preferences}
						</a>
					</div>
				</>
			</Modal>
		</>
	)
}

export default CookiesConsentBanner
